import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Chart } from 'chart.js/auto';

/**
 * Creates a gradient for the background of the chart.
 * @param {CanvasRenderingContext2D} ctx - The context of the canvas.
 * @param {object} area - The chart area.
 * @returns {CanvasGradient}
 */
const createGradient = (ctx, area) => {
    const gradient = ctx.createLinearGradient(0, area.bottom, 0, area.top);
    gradient.addColorStop(0, 'rgba(30, 132, 73, 0)');
    gradient.addColorStop(0.5, 'rgba(30, 132, 73, 0.2)');
    gradient.addColorStop(1, 'rgba(30, 132, 73, 0.5)');
    return gradient;
};

/**
 * Creates a chart using Chart.js and returns it as a Data URL image.
 * @param {Array} data - Data for the chart.
 * @returns {Promise<string|null>} The chart image as a Data URL or null.
 */
const createChartAsImage = async (data) => {
    const sortedData = [...data].sort((a, b) => new Date(a.data) - new Date(b.data));

    if (sortedData.length < 2) {
        return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 350;
    const ctx = canvas.getContext('2d');

    return new Promise(resolve => {
        new Chart(ctx, {
            type: 'line',
            data: {
                labels: sortedData.map(d => new Date(d.data).toLocaleDateString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: '2-digit' })),
                datasets: [{
                    label: 'Altura da Desrama (m)',
                    data: sortedData.map(d => parseFloat(String(d.altura).replace(',', '.'))),
                    borderColor: '#1E8449',
                    backgroundColor: (context) => {
                        const chart = context.chart;
                        const { ctx, chartArea } = chart;
                        if (!chartArea) return null;
                        return createGradient(ctx, chartArea);
                    },
                    fill: true,
                    tension: 0.4,
                    pointBackgroundColor: '#FFFFFF',
                    pointBorderColor: '#1E8449',
                    pointBorderWidth: 2,
                    pointRadius: 4,
                    pointHoverRadius: 6
                }]
            },
            options: {
                responsive: false,
                animation: {
                    onComplete: () => {
                        setTimeout(() => resolve(canvas.toDataURL('image/png', 1.0)), 200);
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        title: { display: true, text: 'Altura (m)', font: { size: 12, family: 'Helvetica' } },
                        grid: { color: '#eef2f5' }
                    },
                    x: {
                        title: { display: false },
                        grid: { display: false }
                    }
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: true,
                        backgroundColor: '#1A2E35',
                        titleFont: { size: 12, weight: 'bold', family: 'Helvetica' },
                        bodyFont: { size: 11, family: 'Helvetica' },
                        padding: 10,
                        cornerRadius: 4,
                        displayColors: false
                    }
                },
                layout: {
                    padding: 10
                }
            }
        });
    });
};

/**
 * Generates a modern PDF report with the selected pruning data.
 * @param {object} params - Parameters for report generation.
 */
export const generateDesramaReport = async ({ selectedData, formatDate, formatNumber, imovelId, logo }) => {
    if (selectedData.length === 0) {
        alert("Por favor, selecione pelo menos um registro para gerar o relatório.");
        return;
    }

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const mainTextColor = '#1A2E35';
    const secondaryTextColor = '#64748b';
    const primaryColor = '#1E8449';

    // --- HEADER ---
    doc.addImage(logo, 'PNG', margin, 12, 35, 12);
    
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(mainTextColor);
    doc.text("Relatório de Desrama", pageWidth - margin, 18, { align: 'right' });

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(secondaryTextColor);
    doc.text(`Análise de Atividade do Imóvel: ${imovelId}`, pageWidth - margin, 25, { align: 'right' });
    
    doc.setDrawColor(238, 242, 245);
    doc.line(margin, 35, pageWidth - margin, 35);

    let startY = 48;

    // --- SUMMARY CARDS ---
    const alturas = selectedData.map(d => parseFloat(String(d.altura).replace(',', '.'))).filter(h => !isNaN(h));
    const alturaMedia = alturas.reduce((a, b) => a + b, 0) / alturas.length;
    const alturaMax = Math.max(...alturas);

    const summaryData = [
        { label: "Total de Registros", value: selectedData.length },
        { label: "Altura Média", value: `${formatNumber(alturaMedia)} m` },
        { label: "Altura Máxima", value: `${formatNumber(alturaMax)} m` }
    ];
    
    const cardWidth = (pageWidth - (margin * 2) - 10) / 3;
    summaryData.forEach((item, index) => {
        const cardX = margin + (index * (cardWidth + 5));
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(mainTextColor);

        // ===== THE FIX IS HERE =====
        // We ensure item.value is a string before passing it to doc.text()
        doc.text(String(item.value), cardX, startY);
        
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(secondaryTextColor);
        doc.text(item.label, cardX, startY + 5);
    });

    startY += 15;

    // --- CHART ---
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(mainTextColor);
    doc.text("Evolução da Altura por Data", margin, startY);
    startY += 6;

    const chartImage = await createChartAsImage(selectedData);
    if (chartImage) {
        const chartWidth = pageWidth - (margin * 2);
        const chartHeight = chartWidth / 2.3;
        doc.addImage(chartImage, 'PNG', margin, startY, chartWidth, chartHeight);
        startY += chartHeight + 12;
    } else {
        doc.setFont('Helvetica', 'italic');
        doc.setFontSize(10);
        doc.text("Não há dados suficientes para gerar um gráfico de evolução (mínimo de 2 registros).", margin, startY);
        startY += 10;
    }

    // --- TABLE ---
    const head = [["Número", "Altura Registrada (m)", "Data do Registro"]];
    const body = selectedData.map(desrama => [
        desrama.numero,
        formatNumber(desrama.altura),
        formatDate(desrama.data)
    ]);

    doc.autoTable({
        head,
        body,
        startY,
        theme: 'striped',
        styles: {
            font: 'Helvetica',
            fontSize: 9,
            cellPadding: 3,
            valign: 'middle'
        },
        headStyles: {
            fillColor: primaryColor,
            textColor: '#FFFFFF',
            fontStyle: 'bold',
            fontSize: 10
        },
        didDrawPage: (data) => {
            // --- FOOTER ---
            const pageCount = doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.setFont('Helvetica', 'normal');
            doc.setTextColor(secondaryTextColor);
            doc.text(
                `Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`,
                margin,
                doc.internal.pageSize.getHeight() - 10
            );
            doc.text(
                `Página ${data.pageNumber} de ${pageCount}`,
                pageWidth - margin,
                doc.internal.pageSize.getHeight() - 10,
                { align: 'right' }
            );
        }
    });

    doc.save(`Relatorio_Desrama_Imovel_${imovelId}.pdf`);
};